import { useMemo, useCallback, useState, useEffect } from 'react'

import { DriverNode, useAllDriversQuery } from '@/apollo/graphql'
import { IOption, IPageInfo, Maybe } from '@/types'
import { driverDisplayLabel, getFetchMoreStatus } from '@/utils'

interface IUseSelect {
  loading: boolean
  initial: Maybe<Partial<DriverNode>>
}

const pageSize = 15

const initialPageInfo: IPageInfo = {
  hasNextPage: false,
  offset: 0,
  fetchMoreStatus: 'loading',
}

export const useSelect = ({ loading, initial }: IUseSelect) => {
  const [drivers, setDrivers] = useState<Partial<DriverNode>[]>([])
  const [pageInfo, setPageInfo] = useState<IPageInfo>(initialPageInfo)
  const [search, setSearch] = useState<string>('')

  const { loading: loadingDrivers, networkStatus: driversNetworkStatus } =
    useAllDriversQuery({
      fetchPolicy: 'network-only',
      skip: loading,
      variables: {
        offset: pageInfo?.offset || 0,
        first: pageSize,
        nameIncludes: search,
      },
      onCompleted: res => {
        if (res.allDrivers?.edges) {
          const _drivers: Partial<DriverNode>[] = []
          res.allDrivers.edges.forEach(e => {
            if (e?.node) _drivers.push(e.node)
          })
          setDrivers(prev => [...prev, ..._drivers])
          setPageInfo(prev => ({
            ...prev,
            hasNextPage: res.allDrivers?.pageInfo.hasNextPage,
          }))
        }
      },
    })

  const driverOptions: IOption<string>[] = useMemo(() => {
    let exists = false
    const opts = drivers.map(s => {
      if (!exists && initial?.id === s.id) {
        exists = true
      }
      return {
        label: driverDisplayLabel(s),
        value: s.id || '',
      }
    })

    if (!exists && initial?.id && !search) {
      opts.unshift({
        label: driverDisplayLabel(initial),
        value: initial.id || '',
      })
    }
    return opts
  }, [drivers, initial, search])

  useEffect(() => {
    setPageInfo(prev => ({
      ...prev,
      fetchMoreStatus: getFetchMoreStatus({
        networkStatus: driversNetworkStatus,
        hasNextPage: prev?.hasNextPage,
        withForce: true,
      }),
    }))
  }, [driversNetworkStatus])

  const onFetchMore = useCallback(() => {
    setPageInfo(prev => ({ ...prev, offset: drivers.length }))
  }, [drivers.length])

  const onSearch = useCallback(
    (text: string) => {
      if (search !== text) {
        setDrivers([])
      }
      setSearch(text)
      setPageInfo(initialPageInfo)
    },
    [search],
  )

  return {
    loadingDrivers,
    driverOptions,
    pageInfo,
    onFetchMore,
    onSearch,
  }
}
